<script lang="ts" setup>
	/**
	 * Proposition de course adressée au chauffeur.
	 *
	 * Le dispatch sollicite les trois ou quatre chauffeurs les plus proches en
	 * même temps : le premier qui accepte emporte la course. La modale est donc
	 * conçue pour une décision en quelques secondes — grosses cibles, compte à
	 * rebours visible, et retrait immédiat dès qu'un autre a été plus rapide.
	 *
	 * Elle est montée au niveau de l'application, pas d'un écran : le chauffeur
	 * doit pouvoir répondre quel que soit l'écran affiché.
	 */
	import { computed, watch } from 'vue';
	import { useRouter } from 'vue-router';
	import { useI18n } from 'vue-i18n';
	import { formatDistance, formatDuration } from '@taxi/shared';
	import { ApiException, useInterval, useToast } from '@common/index';
	import { useDriverStore } from '@/stores/DriverStore';

	const router = useRouter();
	const { t, locale } = useI18n();
	const toast = useToast();
	const driverStore = useDriverStore();

	const offer = computed(() => driverStore.topOffer);

	/** Décompte local : la modale doit rester juste même sans nouvel événement. */
	useInterval(() => driverStore.tickOffers(), 1000);

	/** Le cercle se vide sur la durée totale accordée pour répondre. */
	const progress = computed(() => {
		if (!offer.value) {
			return 0;
		}
		const total = Math.max(
			1,
			Math.round(
				(new Date(offer.value.expiresAt).getTime() - new Date(offer.value.createdAt).getTime()) /
					1000,
			),
		);
		return (offer.value.remainingSeconds / total) * 100;
	});

	/** Rouge dans les dernières secondes : l'urgence doit se voir sans lire. */
	const timerColor = computed(() =>
		(offer.value?.remainingSeconds ?? 0) <= 8 ? 'error' : 'primary',
	);

	const accept = toast.callback(
		async () => {
			const rideId = offer.value?.rideId;
			if (!rideId) {
				return;
			}
			const ride = await driverStore.acceptOffer(rideId);
			void router.push({ name: 'driver-ride', params: { id: ride.id } });
		},
		{
			onError: (error) => {
				// Perdre la course est un cas normal, pas une panne : on l'annonce
				// sobrement et on retire la proposition.
				if (error instanceof ApiException && error.code === 'RIDE_ALREADY_TAKEN') {
					toast.info({ message: t('driver.newOffer') });
				}
				if (offer.value) {
					driverStore.closeOffer(offer.value.id);
				}
			},
		},
	);

	const decline = toast.callback(async () => {
		if (offer.value) {
			await driverStore.declineOffer(offer.value.rideId);
		}
	});

	// Vibration à l'arrivée d'une proposition : le téléphone est sur un support,
	// souvent hors du champ de vision immédiat.
	watch(
		() => offer.value?.id,
		(id) => {
			if (id && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
				navigator.vibrate?.([120, 60, 120]);
			}
		},
	);
</script>

<template>
	<v-dialog :model-value="!!offer" persistent max-width="520" class="offer-dialog">
		<v-card v-if="offer" class="offer-dialog_card">
			<div class="offer-dialog_header">
				<v-progress-circular
					:model-value="progress"
					:color="timerColor"
					size="72"
					width="6"
				>
					<span class="text-h6">{{ offer.remainingSeconds }}</span>
				</v-progress-circular>

				<div>
					<div class="offer-dialog_title">{{ t('driver.newOffer') }}</div>
					<div class="offer-dialog_distance">
						{{
							t('driver.distanceToClient', {
								distance: formatDistance(offer.distanceMeters, locale),
							})
						}}
						· {{ formatDuration(offer.etaSeconds) }}
					</div>
				</div>
			</div>

			<v-divider />

			<div class="offer-dialog_route">
				<div class="offer-dialog_point">
					<v-icon color="primary">mdi-map-marker</v-icon>
					<div class="min-width-0">
						<div class="text-caption text-medium-emphasis">{{ t('ride.pickup') }}</div>
						<div class="font-weight-medium text-truncate">
							{{ offer.ride?.pickup.label }}
						</div>
					</div>
				</div>

				<div v-if="offer.ride?.dropoff" class="offer-dialog_point">
					<v-icon>mdi-flag-checkered</v-icon>
					<div class="min-width-0">
						<div class="text-caption text-medium-emphasis">{{ t('ride.dropoff') }}</div>
						<div class="font-weight-medium text-truncate">
							{{ offer.ride.dropoff.label }}
						</div>
					</div>
				</div>

				<div v-if="offer.ride" class="offer-dialog_meta">
					<v-chip size="small" prepend-icon="mdi-account-multiple" variant="tonal">
						{{ offer.ride.passengers }}
					</v-chip>
					<v-chip
						v-if="offer.ride.luggage"
						size="small"
						prepend-icon="mdi-bag-suitcase"
						variant="tonal"
					>
						{{ offer.ride.luggage }}
					</v-chip>
				</div>

				<v-alert v-if="offer.ride?.note" type="info" variant="tonal" density="compact">
					{{ offer.ride.note }}
				</v-alert>
			</div>

			<v-card-actions class="offer-dialog_actions">
				<v-btn
					size="x-large"
					variant="outlined"
					:loading="decline.loading"
					@click="decline()"
				>
					{{ t('driver.decline') }}
				</v-btn>
				<v-btn
					size="x-large"
					color="primary"
					variant="flat"
					:loading="accept.loading"
					@click="accept()"
				>
					{{ t('driver.accept') }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style lang="scss">
	.offer-dialog {
		&_card {
			border-radius: 18px;
		}

		&_header {
			display: flex;
			align-items: center;
			gap: 18px;
			padding: 20px;
		}

		&_title {
			font-size: 1.35rem;
			font-weight: 700;
		}

		&_distance {
			color: rgba(var(--v-theme-on-surface), 0.7);
		}

		&_route {
			display: grid;
			gap: 14px;
			padding: 18px 20px;
		}

		&_point {
			display: flex;
			align-items: center;
			gap: 12px;
		}

		&_meta {
			display: flex;
			gap: 8px;
		}

		&_actions {
			padding: 12px 20px 20px;
			gap: 12px;

			> .v-btn {
				flex: 1;
			}
		}
	}
</style>
